import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Trash2, Edit2, Loader2, Plus, Briefcase, GraduationCap } from 'lucide-react';
import EditMentorModal from './EditMentorModal';

const AdminMentorList = () => {
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingMentor, setEditingMentor] = useState(null);
    const [isAddMode, setIsAddMode] = useState(false);

    const fetchMentors = async () => {
        try {
            setLoading(true);
            const response = await api.get('/mentors');
            setMentors(Array.isArray(response.data) ? response.data : []);
            setError('');
        } catch (err) {
            console.error('Failed to fetch mentors:', err);
            setError('Failed to load mentors.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMentors();
    }, []);

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this mentor?')) return;
        try {
            await api.delete(`/mentors/${id}`);
            setMentors(mentors.filter(m => m._id !== id));
        } catch (err) {
            console.error('Failed to delete mentor:', err);
            setError('Failed to delete mentor.');
        }
    };

    const handleMentorSaved = (savedMentor, isNew) => {
        if (isNew) {
            setMentors([...mentors, savedMentor]);
        } else {
            setMentors(mentors.map(m => m._id === savedMentor._id ? savedMentor : m));
        }
        setEditingMentor(null);
        setIsAddMode(false);
    };

    if (loading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Manage Mentors</h3>
                <button
                    onClick={() => { setEditingMentor({}); setIsAddMode(true); }}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors font-medium text-sm"
                >
                    <Plus className="w-4 h-4" />
                    Add Mentor
                </button>
            </div>

            {error && (
                <div className="p-4 bg-red-50 text-red-800 rounded-lg text-sm mb-4">
                    {error}
                </div>
            )}

            {(!mentors || mentors.length === 0) && !error ? (
                <div className="text-center p-8 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    No mentors found. Add one to get started.
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {mentors.map((mentor) => {
                        if (!mentor) return null;
                        return (
                            <div key={mentor._id || Math.random()} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col transition-all hover:shadow-md">
                                <div className="flex items-start gap-4 mb-4">
                                    <img
                                        src={mentor.image || ''}
                                        alt={mentor.name}
                                        className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100 flex-shrink-0"
                                        onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=' + mentor.name }}
                                    />
                                    <div>
                                        <h4 className="font-bold text-gray-900 line-clamp-1 flex items-center gap-2">
                                            {mentor.name}
                                        </h4>
                                        <p className="text-sm text-gray-500 line-clamp-1 mb-1">{mentor.role}</p>
                                        <div className="flex items-center gap-2">
                                            <img src={mentor.companyLogo} alt={mentor.company} className="h-4 object-contain max-w-[60px]" onError={(e) => e.target.style.display = 'none'} />
                                            <span className="text-xs text-gray-500">{mentor.company}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="text-sm text-gray-600 space-y-2 mb-4 flex-1">
                                    <div className="flex items-center gap-2">
                                        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                                            Order: {mentor.order || 0}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Briefcase className="w-4 h-4 text-gray-400" />
                                        <span>{mentor.workExp} Work Exp</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <GraduationCap className="w-4 h-4 text-gray-400" />
                                        <span>{mentor.teachingExp} Teaching Exp</span>
                                    </div>
                                    <p className="text-xs text-gray-500 line-clamp-2 mt-2 bg-gray-50 p-2 rounded">{mentor.bio}</p>
                                </div>

                                <div className="flex items-center gap-2 pt-4 border-t border-gray-100 justify-end mt-auto">
                                    <button
                                        onClick={() => { setEditingMentor(mentor); setIsAddMode(false); }}
                                        className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 flex-1 border border-blue-100"
                                    >
                                        <Edit2 className="w-4 h-4" /> Edit
                                    </button>
                                    <button
                                        onClick={() => handleDelete(mentor._id)}
                                        className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1.5 flex-1 border border-red-100"
                                    >
                                        <Trash2 className="w-4 h-4" /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <EditMentorModal
                isOpen={!!editingMentor}
                onClose={() => { setEditingMentor(null); setIsAddMode(false); }}
                mentor={editingMentor}
                isAddMode={isAddMode}
                onMentorSaved={handleMentorSaved}
            />
        </div>
    );
};

export default AdminMentorList;
